import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO } from '../lib/seo';
import { FileText, Megaphone, Users } from 'lucide-react';
import { StockHeroStrip, StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function Press() {
  useSEO({
    title: 'Press | TENSION',
    description:
      'Press resources for TENSION: company overview, media angles, founder contact, and product facts for journalists and creators.',
    keywords: ['TENSION press', 'media kit', 'relationship app press', 'founder interview'],
    canonical: '/press',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="marketing-panel grid gap-6 md:grid-cols-[1.05fr_0.95fr] md:items-center">
          <div className="p-6 md:p-10">
            <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-violet-800 ring-1 ring-violet-200/80">
              Press & Media
            </p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">TENSION media page</h1>
            <p className="mt-3 text-neutral-600 md:text-lg">
              TENSION helps people catch relationship tension early and take calmer action before conflict escalates.
            </p>
          </div>
          <StockImage
            stockKey="supportCalm"
            width={800}
            height={560}
            priority
            alt="Professional context — illustrative stock photo for press page"
            className="h-52 w-full object-cover object-[center_20%] md:h-full md:min-h-[260px] md:rounded-none md:rounded-r-3xl"
          />
        </header>

        <StockHeroStrip stockKey="coupleOutdoor" alt="Connection and wellbeing — illustrative stock imagery" />

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md transition-shadow hover:shadow-lg">
            <Megaphone className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Press angles</h2>
            <p className="mt-2 text-sm text-neutral-600">
              Preventive relationship support, daily signal tracking, and AI draft coaching.
            </p>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md transition-shadow hover:shadow-lg">
            <FileText className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Company facts</h2>
            <p className="mt-2 text-sm text-neutral-600">Mission, product summary, and roadmap context for coverage.</p>
          </Card>
          <Card className="border-violet-100/90 bg-white/95 p-6 shadow-md transition-shadow hover:shadow-lg">
            <Users className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Interview requests</h2>
            <p className="mt-2 text-sm text-neutral-600">Founder and product interview requests are handled quickly.</p>
          </Card>
        </div>

        <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/95 to-fuchsia-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Media contact</h2>
          <p className="mt-2 text-sm text-neutral-600">
            For creator partnerships and press requests, use the business contact form so requests are routed with
            priority context.
          </p>
          <Link to="/business-inquiries" className="mt-5 inline-flex">
            <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md">
              Open business inquiries form
            </Button>
          </Link>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
